import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Table from "../../../../components/Table";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal";
import Input from "../../../../components/Input";
import InputSelect from "../../../../components/InputSelect";
import { ModalDelete } from "../../../../components/ModalDelete";

import api from "../../../../services/api";
import { renderError } from "../../../../helpers/errors";
import { convertIntToMoney, convertMoenyToInt } from "../../../../helpers/conversions";
import { getBillingCompanyLabel, useBillingCompanies } from "../../billingCompany";
import OpenFinanceButton, { ModalOpenFinance, OPEN_FINANCE_LABELS } from "../../OpenFinance";
import { canUseOpenFinance } from "../../../../services/auth";

const TYPE_OPTIONS = [
    { label: 'Conta bancária', value: 'bank' },
    { label: 'Caixa', value: 'cash' },
];

const ACTIVE_OPTIONS = [
    { label: 'Ativa', value: true },
    { label: 'Inativa', value: false },
];

export const BankAccountsTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [accounts, setAccounts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [accountToEdit, setAccountToEdit] = useState(null);
    const [accountToDelete, setAccountToDelete] = useState(null);
    const [accountToConnect, setAccountToConnect] = useState(null);

    const [name, setName] = useState('');
    const [type, setType] = useState('bank');
    const [bankName, setBankName] = useState('');
    const [agency, setAgency] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [initialBalance, setInitialBalance] = useState('R$ 0,00');
    const [active, setActive] = useState(true);
    const [billingCompanyId, setBillingCompanyId] = useState(null);

    const { billingCompanies, hasMultiple: hasMultipleBillingCompanies } = useBillingCompanies();

    // Uma conta é de um CNPJ só. A opção vazia existe para as contas que
    // nasceram antes da coluna e não pode ser escolhida numa conta nova.
    const billingCompanyOptions = [
        ...(accountToEdit && !accountToEdit.billing_company_id ? [{ label: 'Não definida', value: null }] : []),
        ...billingCompanies.map(company => ({ label: getBillingCompanyLabel(company), value: company.id })),
    ];

    const showOpenFinance = canUseOpenFinance();

    const missingBillingCompany = hasMultipleBillingCompanies && !accountToEdit && !billingCompanyId;

    useEffect(() => {
        getAccounts();
    }, []);

    const getAccounts = () => {
        setLoading(true);

        api.get(`/finance-bank-account`).then(res => {
            setAccounts(res.data.resources);
        }).catch(renderError).then(() => setLoading(false));
    };

    const openModal = account => {
        setAccountToEdit(account);
        setName(account?.name ?? '');
        setType(account?.type ?? 'bank');
        setBankName(account?.bank_name ?? '');
        setAgency(account?.agency ?? '');
        setAccountNumber(account?.account_number ?? '');
        setInitialBalance(convertIntToMoney(account?.initial_balance ?? 0, true));
        setActive(account?.active ?? true);
        setBillingCompanyId(account?.billing_company_id ?? null);
        setShowModal(true);
    };

    const save = () => {
        setSaving(true);

        const data = {
            name,
            type,
            bank_name: bankName || null,
            agency: agency || null,
            account_number: accountNumber || null,
            initial_balance: convertMoenyToInt(initialBalance),
            active,
            billing_company_id: billingCompanyId,
        };

        const request = accountToEdit
            ? api.put(`/finance-bank-account/${accountToEdit.id}`, data)
            : api.post(`/finance-bank-account`, data);

        request.then(() => {
            toast.success('Conta salva');
            setShowModal(false);
            getAccounts();
        }).catch(renderError).then(() => setSaving(false));
    };

    const deleteAccount = () => {
        setDeleting(true);

        api.delete(`/finance-bank-account/${accountToDelete.id}`).then(() => {
            setAccountToDelete(null);
            getAccounts();
        }).catch(renderError).then(() => setDeleting(false));
    };

    const getAccountCompanyLabel = account => {
        if (!account.billing_company_id) return 'Não definida';
        const company = billingCompanies.find(item => item.id === account.billing_company_id);
        return company ? getBillingCompanyLabel(company) : '—';
    };

    const rows = accounts.map(account => ({
        data: [
            account.name,
            TYPE_OPTIONS.find(option => option.value === account.type)?.label ?? account.type,
            ...(hasMultipleBillingCompanies ? [getAccountCompanyLabel(account)] : []),
            account.bank_name ?? '',
            convertIntToMoney(account.initial_balance, true),
            account.active ? 'Ativa' : 'Inativa',
            ...(showOpenFinance ? [account.type === 'bank' ? (OPEN_FINANCE_LABELS[account.open_finance_status] ?? 'Não conectada') : '—'] : []),
        ],
        buttons: [
            ...(showOpenFinance && account.type === 'bank'
                ? [<Button type={`secondary`} size={`small`} svg={`connected`} text={`Open Finance`} action={() => setAccountToConnect(account)} />]
                : []),
            <Button type={`delete`} svg={`delete`} action={() => setAccountToDelete(account)} />,
        ],
        action: () => openModal(account),
    }));

    return (
        <>
            <ModalDelete
                show={accountToDelete !== null}
                text={<p>Tem certeza que deseja apagar a conta <strong>{accountToDelete?.name}</strong>?</p>}
                action={deleteAccount}
                loading={deleting}
                close={() => setAccountToDelete(null)}
            />
            <ModalOpenFinance
                show={accountToConnect !== null}
                close={() => {
                    setAccountToConnect(null);
                    getAccounts();
                }}
                bankAccount={accountToConnect}
            />
            <Modal
                title={accountToEdit ? 'Editar conta' : 'Nova conta'}
                show={showModal}
                close={() => setShowModal(false)}
                footer={<Button type={`primary`} size={`small`} text={`Salvar`} action={save} loading={saving} disabled={missingBillingCompany} />}
            >
                <div className="section">
                    <div className="row">
                        <div className="col-8">
                            <Input label={`Nome`} value={name} change={setName} />
                        </div>
                        <div className="col-4">
                            <InputSelect label={`Tipo`} options={TYPE_OPTIONS} value={type} change={setType} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-4">
                            <Input label={`Banco`} value={bankName} change={setBankName} />
                        </div>
                        <div className="col-4">
                            <Input label={`Agência`} value={agency} change={setAgency} />
                        </div>
                        <div className="col-4">
                            <Input label={`Número da conta`} value={accountNumber} change={setAccountNumber} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <Input label={`Saldo inicial`} value={initialBalance} change={setInitialBalance} mask={`convertToMoney`} />
                        </div>
                        <div className="col-6">
                            <InputSelect label={`Situação`} options={ACTIVE_OPTIONS} value={active} change={setActive} />
                        </div>
                    </div>
                    {hasMultipleBillingCompanies &&
                        <div className="row">
                            <div className="col-12">
                                <InputSelect label={`Empresa`} options={billingCompanyOptions} value={billingCompanyId} change={setBillingCompanyId} />
                                {missingBillingCompany &&
                                    <span className={`finance-field-hint`}>
                                        Escolha de qual empresa é esta conta — o saldo por empresa depende disso.
                                    </span>
                                }
                            </div>
                        </div>
                    }
                </div>
            </Modal>

            <div className={`bank-accounts-toolbar`}>
                <OpenFinanceButton />
                <Button type={`primary`} size={`small`} svg={`plus-white`} text={`Nova conta`} action={() => openModal(null)} />
            </div>
            <Table
                header={['Nome', 'Tipo', ...(hasMultipleBillingCompanies ? ['Empresa'] : []), 'Banco', 'Saldo inicial', 'Situação', ...(showOpenFinance ? ['Open Finance'] : []), '']}
                rows={rows}
                loading={loading}
            />
        </>
    );
};

export default BankAccountsTab;
